import { SCHEMA } from '@vedalib/editor/lib/brand'
import { FileUsageTypeEnum } from '@vedalib/editor/lib/files'
import React, { useCallback, useMemo } from 'react'

import Padding from 'components/Editor/Toolbar/Padding'
import { LayoutScroll } from 'components/LayoutPage'
import {
  FieldColorPicker,
  FieldFileId,
  FieldNumber,
  FieldSegmented,
  FieldSlider,
  FormField,
} from 'components/formKit'
import { TileButton } from 'components/uiKit/Button'
import { Typography } from 'components/uiKit/Typography'
import { BrandFieldShadow, BrandTagEdited } from 'services/Branding/components'
import { BORDER_OPTIONS } from 'services/Branding/constants'
import {
  useBrandColorOptions,
  useShowBorder,
  useThemeValueGetter,
} from 'services/Branding/fieldConfigs/useFieldConfigs'
import { isAnyEdited } from 'services/Branding/fieldConfigs/utils'
import { genSchema, genBrandField } from 'services/Branding/genSchema'
import { useUpdateSchema } from 'services/Branding/hooks'
import { BrandFormEnum } from 'services/Branding/types'
import { t } from 'services/Translation'

import type { SimulatorFormBrandType } from './simulatorTypes'

export const STYLE_FORM_SCHEMA = genSchema(SCHEMA.simulator.style, {
  image: {
    borderRadius: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderRadius'),
      props: { min: 0, max: 50, showInput: true },
      useConfigs: [useThemeValueGetter],
    }),
    borderStyle: genBrandField({
      as: FieldSegmented<'none' | 'solid'>,
      label: t('input.label.border'),
      props: { options: BORDER_OPTIONS },
      useConfigs: [useThemeValueGetter],
    }),
    borderColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.borderColor'),
      useConfigs: [useBrandColorOptions, useShowBorder],
    }),
    borderWidth: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderWidth'),
      props: { min: 1, max: 8, showInput: true },
      useConfigs: [useShowBorder],
    }),
  },
  area: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
    borderColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.borderColor'),
      useConfigs: [useBrandColorOptions, useShowBorder],
    }),
  },
  textarea: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
    borderRadius: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderRadius'),
      props: { min: 0, max: 50, showInput: true },
      useConfigs: [useThemeValueGetter],
    }),
    borderStyle: genBrandField({
      as: FieldSegmented<'none' | 'solid'>,
      label: t('input.label.border'),
      props: { options: BORDER_OPTIONS },
      useConfigs: [useThemeValueGetter],
    }),
    borderColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.borderColor'),
      useConfigs: [useBrandColorOptions, useShowBorder],
    }),
    borderWidth: genBrandField({
      as: FieldSlider,
      label: t('input.label.borderWidth'),
      props: { min: 1, max: 8, showInput: true },
      useConfigs: [useShowBorder],
    }),
  },
  hoverTextarea: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('style.hoverColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  focusTextarea: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('style.focusColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  errorTextarea: {
    borderColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('common.error'),
      useConfigs: [useBrandColorOptions],
    }),
  },
  hint: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
    backgroundImage: genBrandField({
      as: FieldFileId,
      label: t('elements.simulator.brand.closeIcon.label'),
      layout: 'vertical',
      props: { fileType: FileUsageTypeEnum.image, preview: false },
    }),
  },
  navigation: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('input.label.backgroundColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  hoverButton: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('elements.simulator.schema.hoverColor'),
      props: { transparent: true, labeled: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  icon: {
    color: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('style.iconColor'),
      useConfigs: [useBrandColorOptions],
    }),
  },
  backButton: {
    backgroundImage: genBrandField({
      as: FieldFileId,
      label: t('elements.simulator.schema.prevIcon'),
      layout: 'vertical',
      props: { fileType: FileUsageTypeEnum.image, preview: false },
    }),
  },
  nextButton: {
    backgroundImage: genBrandField({
      as: FieldFileId,
      label: t('elements.simulator.schema.nextIcon'),
      layout: 'vertical',
      props: { fileType: FileUsageTypeEnum.image, preview: false },
    }),
  },
  indents: {
    gap: genBrandField({
      as: FieldNumber,
      label: t('elements.simulator.schema.buttonsGap'),
      rules: [{ min: 0, message: ' ' }],
      props: { width: 56 },
    }),
    marginLeft: genBrandField({
      as: FieldNumber,
      label: t('elements.simulator.schema.textIndent'),
      rules: [{ min: 0, message: ' ' }],
      props: { width: 56 },
    }),
    marginTop: genBrandField({
      as: FieldNumber,
      label: t('elements.simulator.schema.navIndent'),
      rules: [{ min: 0, message: ' ' }],
      props: { width: 56 },
    }),
  },
  hover: {
    backgroundColor: genBrandField({
      as: FieldColorPicker<string | null>,
      label: t('style.hoverColor'),
      props: { transparent: true, labeled: true, resettable: true },
      useConfigs: [useBrandColorOptions],
    }),
  },
  button_effects: {
    boxShadow: genBrandField({
      as: BrandFieldShadow,
      label: t('elements.simulator.schema.boxShadow'),
      useConfigs: [useThemeValueGetter],
    }),
  },
  effects: {
    boxShadow: genBrandField({
      as: BrandFieldShadow,
      label: t('elements.simulator.schema.shadow'),
      useConfigs: [useThemeValueGetter],
    }),
    animation: genBrandField({
      as: FieldSegmented<boolean>,
      label: t('elements.simulation.schema.animation'),
    }),
  },
  textAreaEffects: {
    boxShadow: genBrandField({
      as: BrandFieldShadow,
      label: t('elements.simulator.schema.textShadow'),
      useConfigs: [useThemeValueGetter],
    }),
  },
})

const SimulatorFormStyle: SimulatorFormBrandType = ({ edited, device, subConfigs, setForm }) => {
  const path = useMemo(() => ['style', device], [device])
  const schema = useUpdateSchema(path, subConfigs, STYLE_FORM_SCHEMA)

  const handleSetTypography = useCallback(() => setForm?.(BrandFormEnum.TYPOGRAPHY), [setForm])

  return (
    <LayoutScroll sizeAutoCapable>
      <Padding bottom bottomSpace gap sides>
        <Typography weight='medium'>{t('elements.simulator.tags.item')}</Typography>
        <FormField config={schema.image.borderRadius} />
        <FormField config={schema.image.borderStyle} />
        <FormField config={schema.image.borderColor} />
        <FormField config={schema.image.borderWidth} />

        <Typography weight='medium'>{t('elements.simulator.tags.area')}</Typography>
        <FormField config={schema.area.backgroundColor} />
        <FormField config={schema.area.borderColor} />
        <FormField config={schema.hover.backgroundColor} />

        <Typography weight='medium'>{t('elements.simulator.tags.textarea')}</Typography>
        <FormField config={schema.textarea.backgroundColor} />
        <FormField config={schema.textarea.borderRadius} />
        <FormField config={schema.textarea.borderStyle} />
        <FormField config={schema.textarea.borderColor} />
        <FormField config={schema.textarea.borderWidth} />
        <FormField config={schema.hoverTextarea.backgroundColor} />
        <FormField config={schema.focusTextarea.backgroundColor} />
        <FormField config={schema.errorTextarea.borderColor} />

        <Typography weight='medium'>{t('elements.simulator.tags.hint')}</Typography>
        <FormField config={schema.hint.backgroundColor} />
        <FormField config={schema.hint.backgroundImage} />

        <Typography weight='medium'>{t('elements.simulator.tags.navigation')}</Typography>
        <FormField config={schema.navigation.backgroundColor} />
        <FormField config={schema.hoverButton.backgroundColor} />
        <FormField config={schema.icon.color} />
        <FormField config={schema.backButton.backgroundImage} />
        <FormField config={schema.nextButton.backgroundImage} />
        <FormField config={schema.indents.gap} />
        <FormField config={schema.indents.marginLeft} />
        <FormField config={schema.indents.marginTop} />

        <Typography weight='medium'>{t('style.effects')}</Typography>
        <FormField config={schema.effects.boxShadow} />
        <FormField config={schema.button_effects.boxShadow} />
        <FormField config={schema.effects.animation} />
        <FormField config={schema.textAreaEffects.boxShadow} />

        <TileButton
          description={t('uiKit.button.richTextDescription')}
          icon='otherRight'
          name='showFontForm'
          onClick={handleSetTypography}
        >
          {t('page.brand.tab.typography')} {isAnyEdited(edited.font) && <BrandTagEdited />}
        </TileButton>
      </Padding>
    </LayoutScroll>
  )
}

export default React.memo(SimulatorFormStyle) as typeof SimulatorFormStyle
